import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ColorThief from 'colorthief';
import {
  Palette,
  Save,
  RotateCcw,
  Loader2,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Eye,
  ImageIcon,
  Upload,
  X
} from 'lucide-react';
import { gamesAPI } from '../api/games';
import { reportImagesAPI } from '../api/clientReports';

// Helper to convert RGB array to hex
const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

// Color presets for quick selection
const COLOR_PRESETS = [
  { name: 'Indigo', primary: '#4f46e5', secondary: '#8b5cf6', accent: '#10b981' },
  { name: 'Red', primary: '#dc2626', secondary: '#ef4444', accent: '#f59e0b' },
  { name: 'Blue', primary: '#2563eb', secondary: '#3b82f6', accent: '#06b6d4' },
  { name: 'Green', primary: '#16a34a', secondary: '#22c55e', accent: '#84cc16' },
  { name: 'Purple', primary: '#9333ea', secondary: '#a855f7', accent: '#ec4899' },
  { name: 'Orange', primary: '#ea580c', secondary: '#f97316', accent: '#fbbf24' },
  { name: 'Teal', primary: '#0d9488', secondary: '#14b8a6', accent: '#06b6d4' },
  { name: 'Slate', primary: '#475569', secondary: '#64748b', accent: '#94a3b8' },
];

const FONT_OPTIONS = [
  'Helvetica',
  'Arial',
  'Georgia',
  'Times New Roman',
  'Verdana',
  'Roboto',
  'Open Sans',
  'Montserrat',
];

// Background image display modes
const BG_IMAGE_MODES = [
  { value: 'none', label: 'None' },
  { value: 'cover', label: 'Stretched' },
  { value: 'contain', label: 'Fit' },
  { value: 'tile', label: 'Tile' },
  { value: 'center', label: 'Center' },
];

// Compact color input component
const ColorInput = ({ label, value, onChange }) => (
  <div className="flex items-center gap-2">
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-6 h-6 rounded cursor-pointer border border-gray-300 p-0"
    />
    <span className="text-xs text-gray-600 w-16">{label}</span>
  </div>
);

const GameBrandingEditor = ({ gameId, gameName, initialBranding, onSave }) => {
  const queryClient = useQueryClient();
  const logoImgRef = useRef(null);
  const bgImageInputRef = useRef(null);
  const justSavedRef = useRef(false); // Track if we just saved to prevent useEffect from overwriting
  const [expanded, setExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [bgUploadStatus, setBgUploadStatus] = useState(null); // 'uploading', 'success', 'error'
  const [branding, setBranding] = useState({
    primaryColor: '#4f46e5',
    secondaryColor: '#8b5cf6',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    accentColor: '#10b981',
    logoUrl: '',
    bannerUrl: '',
    backgroundImageUrl: '',
    backgroundImageMode: 'none',
    fontFamily: 'Helvetica',
    ...initialBranding
  });
  const [saveStatus, setSaveStatus] = useState(null);
  const [logoPreset, setLogoPreset] = useState(null);

  // Check if we can save (need gameId)
  const canSave = !!gameId;

  // Track if local changes have been made to avoid overwriting with stale initialBranding
  const hasLocalChangesRef = useRef(false);

  useEffect(() => {
    // Skip sync if we just saved - our local state is the source of truth
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    // Skip sync if user has made local changes (like uploading a background image)
    if (hasLocalChangesRef.current) {
      return;
    }
    if (initialBranding) {
      setBranding(prev => ({ ...prev, ...initialBranding }));
    }
  }, [initialBranding]);

  // Extract colors from logo when it loads
  const extractColorsFromLogo = (imgElement) => {
    try {
      const colorThief = new ColorThief();
      const palette = colorThief.getPalette(imgElement, 5);
      if (palette && palette.length >= 3) {
        const colors = palette.map(rgb => rgbToHex(rgb[0], rgb[1], rgb[2]));
        setLogoPreset({
          name: 'From Logo',
          primary: colors[0],
          secondary: colors[1],
          accent: colors[2],
          allColors: colors
        });
      }
    } catch (err) {
      console.log('Could not extract colors from logo:', err);
    }
  };

  const saveMutation = useMutation({
    mutationFn: (data) => gamesAPI.updateBranding(gameId, data),
    onSuccess: (response, sentData) => {
      setSaveStatus('saved');
      // Mark that we just saved to prevent useEffect from overwriting our local state
      justSavedRef.current = true;
      // Use the saved branding from server response, or fall back to sent data
      const savedBranding = response?.data?.branding || sentData;
      if (onSave) onSave(savedBranding);
      setTimeout(() => setSaveStatus(null), 2000);
    },
    onError: (error) => {
      console.error('Failed to save branding:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  });

  const resetMutation = useMutation({
    mutationFn: () => gamesAPI.resetBranding(gameId),
    onSuccess: () => {
      setBranding({
        primaryColor: '#4f46e5',
        secondaryColor: '#8b5cf6',
        backgroundColor: '#ffffff',
        textColor: '#1f2937',
        accentColor: '#10b981',
        logoUrl: '',
        bannerUrl: '',
        backgroundImageUrl: '',
        backgroundImageMode: 'none',
        fontFamily: 'Helvetica',
      });
      queryClient.invalidateQueries(['game-branding', gameId]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  });

  const handleChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  const handlePresetSelect = (preset) => {
    setBranding(prev => ({
      ...prev,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      accentColor: preset.accent
    }));
  };

  const handleSave = () => {
    if (!gameId) {
      console.error('Cannot save branding - no gameId');
      return;
    }
    setSaveStatus('saving');
    saveMutation.mutate(branding);
  };

  const handleReset = () => {
    if (window.confirm('Reset branding to defaults?')) {
      resetMutation.mutate();
    }
  };

  // Handle background image upload
  const handleBgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setBgUploadStatus('uploading');

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;

        try {
          const response = await reportImagesAPI.upload({
            filename: file.name,
            mimeType: file.type,
            data: base64Data
          });

          if (response.data?.id) {
            // Use the uploaded image URL
            const imageUrl = reportImagesAPI.getImageUrl(response.data.id);
            // Mark that we have local changes to prevent useEffect from overwriting
            hasLocalChangesRef.current = true;
            // Set both fields at once to avoid state race conditions
            setBranding(prev => ({
              ...prev,
              backgroundImageUrl: imageUrl,
              backgroundImageMode: (!prev.backgroundImageMode || prev.backgroundImageMode === 'none') ? 'cover' : prev.backgroundImageMode
            }));
            setBgUploadStatus('success');
            setTimeout(() => setBgUploadStatus(null), 2000);
          }
        } catch (err) {
          console.error('Upload error:', err);
          setBgUploadStatus('error');
          setTimeout(() => setBgUploadStatus(null), 3000);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('File read error:', err);
      setBgUploadStatus('error');
      setTimeout(() => setBgUploadStatus(null), 3000);
    }

    // Reset input
    if (bgImageInputRef.current) {
      bgImageInputRef.current.value = '';
    }
  };

  // Generate background style based on settings
  const getBackgroundStyle = () => {
    const style = { backgroundColor: branding.backgroundColor };

    if (branding.backgroundImageUrl && branding.backgroundImageMode !== 'none') {
      style.backgroundImage = `url(${branding.backgroundImageUrl})`;

      switch (branding.backgroundImageMode) {
        case 'cover':
          style.backgroundSize = 'cover';
          style.backgroundPosition = 'center';
          style.backgroundRepeat = 'no-repeat';
          break;
        case 'contain':
          style.backgroundSize = 'contain';
          style.backgroundPosition = 'center';
          style.backgroundRepeat = 'no-repeat';
          break;
        case 'tile':
          style.backgroundSize = 'auto';
          style.backgroundRepeat = 'repeat';
          break;
        case 'center':
          style.backgroundSize = 'auto';
          style.backgroundPosition = 'center';
          style.backgroundRepeat = 'no-repeat';
          break;
        default:
          break;
      }
    }

    return style;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Compact Header Row */}
      <div className="px-4 py-3 flex items-center justify-between gap-4">
        {/* Left: Title & Current Colors Preview */}
        <div className="flex items-center gap-3">
          <Palette className="w-4 h-4 text-sc-pink" />
          <span className="text-sm font-medium text-gray-900">Report Branding</span>
          {gameName && <span className="text-xs text-gray-500">• {gameName}</span>}

          {/* Current colors mini preview */}
          <div className="flex items-center gap-1 ml-2">
            <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: branding.primaryColor }} title="Primary" />
            <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: branding.secondaryColor }} title="Secondary" />
            <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: branding.accentColor }} title="Accent" />
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {saveStatus && (
            <span className={`flex items-center text-xs ${
              saveStatus === 'saving' ? 'text-gray-500' :
              saveStatus === 'saved' ? 'text-green-600' : 'text-red-600'
            }`}>
              {saveStatus === 'saving' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {saveStatus === 'saved' && <Check className="w-3 h-3 mr-1" />}
              {saveStatus === 'error' && <AlertCircle className="w-3 h-3 mr-1" />}
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Error'}
            </span>
          )}

          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`p-1.5 rounded transition-colors ${showPreview ? 'bg-sc-pink/20 text-sc-pink' : 'hover:bg-gray-100 text-gray-500'}`}
            title="Preview"
          >
            <Eye className="w-4 h-4" />
          </button>

          <button
            onClick={handleSave}
            disabled={saveMutation.isPending || !canSave}
            className="px-3 py-1.5 bg-sc-pink text-white rounded text-xs font-medium hover:bg-sc-pink/90 transition-colors flex items-center disabled:bg-gray-300 disabled:cursor-not-allowed"
            title={canSave ? 'Save branding' : 'No game linked to save branding'}
          >
            {saveMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          </button>

          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
          </button>
        </div>
      </div>

      {/* Compact Presets Row (always visible) */}
      <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
        {/* Logo preset - more prominent */}
        {logoPreset && (
          <>
            <button
              onClick={() => handlePresetSelect(logoPreset)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-150 transition-colors shadow-sm"
            >
              <Sparkles className="w-3 h-3 text-purple-600" />
              <span className="text-xs font-medium text-purple-700">From Logo</span>
              <div className="flex -space-x-1 ml-0.5">
                <div className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: logoPreset.primary }} />
                <div className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: logoPreset.secondary }} />
                <div className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: logoPreset.accent }} />
              </div>
            </button>
            <div className="w-px h-4 bg-gray-300" /> {/* Divider */}
          </>
        )}

        <span className="text-xs text-gray-400">Presets:</span>

        {/* Standard presets */}
        {COLOR_PRESETS.map((preset) => (
          <button
            key={preset.name}
            onClick={() => handlePresetSelect(preset)}
            className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 hover:border-gray-300 transition-colors"
            title={preset.name}
          >
            <div className="flex -space-x-1">
              <div className="w-3 h-3 rounded-full border border-white" style={{ backgroundColor: preset.primary }} />
              <div className="w-3 h-3 rounded-full border border-white" style={{ backgroundColor: preset.secondary }} />
              <div className="w-3 h-3 rounded-full border border-white" style={{ backgroundColor: preset.accent }} />
            </div>
          </button>
        ))}
      </div>

      {/* Compact Preview */}
      {showPreview && (
        <div className="px-4 pb-3">
          <div className="text-[10px] text-gray-400 mb-1 flex items-center gap-2">
            <span>PDF Preview</span>
            {branding.backgroundImageUrl && branding.backgroundImageMode !== 'none' && (
              <span className="text-purple-500">+ Background Image ({BG_IMAGE_MODES.find(m => m.value === branding.backgroundImageMode)?.label})</span>
            )}
          </div>
          <div
            className="rounded-lg p-4 border text-xs relative overflow-hidden"
            style={{ ...getBackgroundStyle(), fontFamily: branding.fontFamily, minHeight: '80px' }}
          >
            {/* Content overlay for readability when there's a background image */}
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <div className="px-2 py-1 rounded text-white text-[10px] font-medium shadow-sm" style={{ backgroundColor: branding.primaryColor }}>
                  Primary
                </div>
                <div className="px-2 py-1 rounded text-white text-[10px] font-medium shadow-sm" style={{ backgroundColor: branding.secondaryColor }}>
                  Secondary
                </div>
                <div className="px-2 py-1 rounded text-white text-[10px] font-medium shadow-sm" style={{ backgroundColor: branding.accentColor }}>
                  Accent
                </div>
              </div>
              <p style={{ color: branding.textColor }}>
                <span className="font-semibold" style={{ color: branding.primaryColor }}>Report Title</span> — Sample text showing how your branding will look in the PDF.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Options */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-3">
          {/* Color Pickers Row */}
          <div className="flex flex-wrap items-center gap-4">
            <ColorInput label="Primary" value={branding.primaryColor} onChange={(v) => handleChange('primaryColor', v)} />
            <ColorInput label="Secondary" value={branding.secondaryColor} onChange={(v) => handleChange('secondaryColor', v)} />
            <ColorInput label="Accent" value={branding.accentColor} onChange={(v) => handleChange('accentColor', v)} />
            <ColorInput label="Background" value={branding.backgroundColor} onChange={(v) => handleChange('backgroundColor', v)} />
            <ColorInput label="Text" value={branding.textColor} onChange={(v) => handleChange('textColor', v)} />

            {/* Font selector */}
            <div className="flex items-center gap-2">
              <select
                value={branding.fontFamily}
                onChange={(e) => handleChange('fontFamily', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-xs"
              >
                {FONT_OPTIONS.map((font) => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Image URLs Row */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block">Logo URL</label>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={branding.logoUrl || ''}
                  onChange={(e) => handleChange('logoUrl', e.target.value)}
                  placeholder="https://..."
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                />
                {branding.logoUrl && (
                  <img
                    ref={logoImgRef}
                    src={branding.logoUrl}
                    alt="Logo"
                    className="h-6 w-auto object-contain"
                    crossOrigin="anonymous"
                    onLoad={(e) => extractColorsFromLogo(e.target)}
                    onError={(e) => { e.target.style.display = 'none'; setLogoPreset(null); }}
                  />
                )}
              </div>
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block">Banner URL</label>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={branding.bannerUrl || ''}
                  onChange={(e) => handleChange('bannerUrl', e.target.value)}
                  placeholder="https://..."
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                />
                {branding.bannerUrl && (
                  <img
                    src={branding.bannerUrl}
                    alt="Banner"
                    className="h-6 w-12 object-cover rounded"
                    crossOrigin="anonymous"
                    onLoad={(e) => { if (!logoPreset) extractColorsFromLogo(e.target); }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Background Image Row */}
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                Background Image
              </label>
              <div className="flex items-center gap-2">
                {/* URL Input */}
                <input
                  type="url"
                  value={branding.backgroundImageUrl || ''}
                  onChange={(e) => handleChange('backgroundImageUrl', e.target.value)}
                  placeholder="https://... or upload"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                />

                {/* Upload Button */}
                <input
                  ref={bgImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleBgImageUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => bgImageInputRef.current?.click()}
                  disabled={bgUploadStatus === 'uploading'}
                  className="px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                  title="Upload image"
                >
                  {bgUploadStatus === 'uploading' ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : bgUploadStatus === 'success' ? (
                    <Check className="w-3 h-3 text-green-600" />
                  ) : bgUploadStatus === 'error' ? (
                    <AlertCircle className="w-3 h-3 text-red-600" />
                  ) : (
                    <Upload className="w-3 h-3" />
                  )}
                </button>

                {/* Preview & Clear */}
                {branding.backgroundImageUrl && (
                  <>
                    <img
                      src={branding.backgroundImageUrl}
                      alt="Background"
                      className="h-6 w-10 object-cover rounded border border-gray-200"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        handleChange('backgroundImageUrl', '');
                        handleChange('backgroundImageMode', 'none');
                      }}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Remove background image"
                    >
                      <X className="w-3 h-3 text-gray-400 hover:text-red-500" />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="w-32">
              <label className="text-xs text-gray-500 mb-1 block">Display Mode</label>
              <select
                value={branding.backgroundImageMode || 'none'}
                onChange={(e) => handleChange('backgroundImageMode', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                disabled={!branding.backgroundImageUrl}
              >
                {BG_IMAGE_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Reset button */}
          <div className="flex justify-end">
            <button
              onClick={handleReset}
              disabled={resetMutation.isPending}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBrandingEditor;
